/**
 * @file JoinChallengesPage.jsx
 * @description Page for finding and joining public challenges
 */

import React, { useEffect, useState, useRef } from "react";
import { useUserAuth } from "../UserAuth";
import { db } from "../firebase";
import { collection, getDocs, query, where, documentId } from "firebase/firestore";
import { checkIfUserIsInChallenge, addUserToChallenge } from "./challenges/challenges";
import { MaterialHeader } from "./MaterialHeader";
import { useViewportHeight } from "../utils/viewportUtils";
import { Icon } from "./Icon";

export const JoinChallengesPage = () => {
  const { user } = useUserAuth();
  const viewportHeight = useViewportHeight();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [joiningChallenge, setJoiningChallenge] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    fetchPublicChallenges();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 2000);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Fetch challenges when debounced search term changes
  useEffect(() => {
    // Only refetch from Firebase when search term changes significantly
    // For now, we'll just refetch to get fresh data, but in the future
    // we could implement more sophisticated server-side filtering
    if (debouncedSearchTerm !== searchTerm && debouncedSearchTerm !== "") {
      fetchPublicChallenges(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPublicChallenges = async (searchQuery = "") => {
    try {
      const isSearching = searchQuery !== "";
      if (isSearching) {
        setSearchLoading(true);
      } else {
        setLoading(true);
      }
      
      // First, get the list of active challenge IDs
      const activeChallengesRef = collection(db, "active_challenges");
      const activeChallengesSnapshot = await getDocs(activeChallengesRef);
      const activeChallengeIds = activeChallengesSnapshot.docs.map(doc => doc.id);
      
      // If no active challenges, return empty array
      if (activeChallengeIds.length === 0) {
        setChallenges([]);
        return;
      }
      
      // Build Firebase query to only get active challenges that are also public
      const challengesRef = collection(db, "challenges");
      
      // If we have more than 10 active challenges, we'll need to make multiple queries
      const challengeQueries = [];
      for (let i = 0; i < activeChallengeIds.length; i += 10) {
        const batch = activeChallengeIds.slice(i, i + 10);
        const batchQuery = query(challengesRef, 
          where("is_public", "==", true),
          where(documentId(), "in", batch)
        );
        challengeQueries.push(getDocs(batchQuery));
      }
      
      // Execute all queries in parallel
      const challengeSnapshots = await Promise.all(challengeQueries);
      const allChallengeDocs = challengeSnapshots.flatMap(snapshot => snapshot.docs);
      
      const challengePromises = allChallengeDocs.map(async (challengeDoc) => {
        const challengeData = challengeDoc.data();
        const challengeId = challengeDoc.id;
        
        // Get participant count
        const participantsRef = collection(db, "challenges", challengeId, "users");
        const participantsSnapshot = await getDocs(participantsRef);
        const participantCount = participantsSnapshot.size;
        
        // Check if user is already in this challenge (only if user is signed in)
        let userIsInChallenge = false;
        if (user) {
          userIsInChallenge = await checkIfUserIsInChallenge(db, user.uid, challengeId);
        }
        
        return {
          id: challengeId,
          ...challengeData,
          participantCount,
          userIsInChallenge
        };
      });
      
      const challengesWithData = await Promise.all(challengePromises);
      
      // Filter out challenges user is already in (if signed in)
      const availableChallenges = user 
        ? challengesWithData.filter(challenge => !challenge.userIsInChallenge)
        : challengesWithData;
      
      // Sort by participant count (descending)
      availableChallenges.sort((a, b) => b.participantCount - a.participantCount);
      
      setChallenges(availableChallenges);
    } catch (error) {
      console.error("Error fetching public challenges:", error);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const handleJoinChallenge = async (challengeId) => {
    if (!user) {
      alert("Please sign in to join a challenge");
      return;
    }
    
    try {
      setJoiningChallenge(challengeId);
      await addUserToChallenge(db, user.uid, challengeId);
      
      // Navigate to the newly joined challenge with proper history manipulation
      // Replace current history entry so back button goes to home instead of join page
      window.history.replaceState(null, null, '/');
      window.location.href = `/challenge?id=${challengeId}`;
      
    } catch (error) {
      console.error("Error joining challenge:", error);
      alert("Failed to join challenge. Please try again.");
      setJoiningChallenge(null);
    }
  };

  // Client-side filtering for immediate feedback while typing
  const filteredChallenges = challenges.filter(challenge => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      challenge.challenge_name.toLowerCase().includes(searchLower) ||
      challenge.books.some(book => book.toLowerCase().includes(searchLower))
    );
  });

  const handleClose = () => {
    window.location.href = '/';
  };

  return (
    <>
      <MaterialHeader 
        variant="close" 
        title="Join a Reading Challenge"
        onClose={handleClose}
      />
      <div className="app-content" style={{ 
        height: `calc(${viewportHeight} - 64px)`, 
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div className="container" style={{ 
          maxWidth: '1200px',
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: '16px'
        }}>

        {/* Search Bar */}
        <div style={{ marginBottom: '24px', flexShrink: 0 }}>
          <div style={{
            position: 'relative',
            maxWidth: '500px'
          }}>
            <input
              type="text"
              placeholder="Search challenges by name or book..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '16px 48px 16px 16px',
                border: '1px solid var(--md-sys-color-outline)',
                borderRadius: '28px',
                fontSize: 'var(--md-sys-typescale-body-large-size)',
                backgroundColor: 'var(--md-sys-color-surface-container-low)',
                color: 'var(--md-sys-color-on-surface)',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--md-sys-color-primary)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--md-sys-color-outline)';
              }}
            />
            {searchLoading ? (
              <div style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '20px',
                height: '20px',
                border: '2px solid var(--md-sys-color-primary)',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
            ) : (
              <Icon
                name="search"
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--md-sys-color-on-surface-variant)',
                  pointerEvents: 'none'
                }}
              />
            )}
          </div>
        </div>


        {/* Challenges Grid */}
        <div style={{ flex: '1', overflow: 'auto', minHeight: 0 }}>
          {loading && (
            <md-elevated-card style={{ padding: '48px', textAlign: 'center' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px',
                color: 'var(--md-sys-color-on-surface-variant)'
              }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  border: '2px solid var(--md-sys-color-primary)',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Loading public challenges...
              </div>
            </md-elevated-card>
          )}

          {!loading && (
            <>
              {filteredChallenges.length === 0 ? (
                <md-elevated-card style={{ padding: '48px', textAlign: 'center', overflow: 'visible' }}>
                <Icon
                  name={searchTerm ? "searchOff" : "group"}
                  size="hero"
                  style={{ 
                    color: 'var(--md-sys-color-outline)',
                    margin: '0 auto 24px'
                  }}
                />
                <h2 style={{ 
                  fontSize: 'var(--md-sys-typescale-headline-medium-size)',
                  color: 'var(--md-sys-color-on-surface)',
                  marginBottom: '16px'
                }}>
                  {searchTerm ? 'No challenges found' : 'No public challenges available'}
                </h2>
                <p style={{ 
                  color: 'var(--md-sys-color-on-surface-variant)',
                  marginBottom: '32px',
                  maxWidth: '500px',
                  margin: '0 auto 32px'
                }}>
                  {searchTerm 
                    ? 'Try adjusting your search terms or browse all challenges.'
                    : user 
                      ? 'You\'re already in all available public challenges, or there are no public challenges yet.'
                      : 'There are no public challenges available at the moment.'
                  }
                </p>
                <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <md-filled-button href="/">
                    <Icon name="home" slot="icon" />
                    Back to Home
                  </md-filled-button>
                  <md-outlined-button href="/create_challenge">
                    <Icon name="add" slot="icon" />
                    Create Your Own Challenge
                  </md-outlined-button>
                </div>
              </md-elevated-card>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                  gap: '24px',
                  padding: '0 0 24px 0'
                }}>
                  {filteredChallenges.map((challenge) => (
                  <md-elevated-card key={challenge.id} style={{ padding: '24px' }}>
                    <div style={{ marginBottom: '16px' }}>
                      <h3 style={{
                        fontSize: 'var(--md-sys-typescale-headline-small-size)',
                        fontWeight: 'var(--md-sys-typescale-headline-small-weight)',
                        color: 'var(--md-sys-color-on-surface)',
                        margin: '0 0 8px 0'
                      }}>
                        {challenge.challenge_name}
                      </h3>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: 'var(--md-sys-color-on-surface-variant)',
                        fontSize: 'var(--md-sys-typescale-body-medium-size)',
                        marginBottom: '12px'
                      }}>
                        <Icon name="group" style={{ fontSize: '18px' }} />
                        {challenge.participantCount} participant{challenge.participantCount !== 1 ? 's' : ''}
                      </div>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <p style={{
                        color: 'var(--md-sys-color-on-surface-variant)',
                        fontSize: 'var(--md-sys-typescale-body-medium-size)',
                        margin: '0 0 8px 0'
                      }}>
                        <strong>Books:</strong> {challenge.books.join(', ')}
                      </p>
                      <p style={{
                        color: 'var(--md-sys-color-on-surface-variant)',
                        fontSize: 'var(--md-sys-typescale-body-medium-size)',
                        margin: '0 0 8px 0'
                      }}>
                        <strong>Reading Days:</strong> {challenge.days.join(', ')}
                      </p>
                      <p style={{
                        color: 'var(--md-sys-color-on-surface-variant)',
                        fontSize: 'var(--md-sys-typescale-body-medium-size)',
                        margin: 0
                      }}>
                        <strong>Start Date:</strong> {new Date(challenge.start_date).toLocaleDateString()}
                      </p>
                    </div>

                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      justifyContent: 'flex-end'
                    }}>
                      <md-outlined-button 
                        href={`/challenge?id=${challenge.id}&tab=info`}
                        style={{ flex: '1' }}
                      >
                        Details
                      </md-outlined-button>
                      
                      {user && (
                        <md-filled-button
                          onClick={() => handleJoinChallenge(challenge.id)}
                          {...(joiningChallenge === challenge.id ? { disabled: true } : {})}
                          style={{ flex: '1' }}
                        >
                          {joiningChallenge === challenge.id ? (
                            <>
                              <div style={{
                                width: '16px',
                                height: '16px',
                                border: '2px solid currentColor',
                                borderTop: '2px solid transparent',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                marginRight: '8px'
                              }}></div>
                              Joining...
                            </>
                          ) : (
                            <>
                              <Icon name="add" slot="icon" />
                              Join Challenge
                            </>
                          )}
                        </md-filled-button>
                      )}
                    </div>

                    {!user && (
                      <div style={{
                        marginTop: '16px',
                        padding: '12px',
                        backgroundColor: 'var(--md-sys-color-surface-container-low)',
                        borderRadius: '12px',
                        textAlign: 'center'
                      }}>
                        <p style={{
                          color: 'var(--md-sys-color-on-surface-variant)',
                          fontSize: 'var(--md-sys-typescale-body-small-size)',
                          margin: 0
                        }}>
                          Sign in to join this challenge
                        </p>
                      </div>
                    )}
                  </md-elevated-card>
                ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Add CSS for spinner animation */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        </div>
      </div>
    </>
  );
};
